/**
 * The icon set, as one drawing.
 *
 * Every glyph in the app is defined here and painted by `Glyph.vue`; `Icon.vue`, `ToolIcon.vue`,
 * `ConstructIcon.vue` and `RadioIcon.vue` are namespaced views onto this one table. They were four
 * separate tables before, and four tables made four sets: `pen` and `pencil` were the same drawing
 * in two files, `chevron` and `caret` were the same idea at two different sizes, and a glyph fixed
 * in one file stayed broken in the other three.
 *
 * ── The grid ──────────────────────────────────────────────────────────────────────────────────
 *
 * 24×24 box. Live area 2.5–21.5; the mass of a glyph stays inside 3.5–20.5 and only a deliberate
 * reach — an axis, a straightedge, a line running off the page — touches the live edge.
 *
 * **Minimum clearance between two strokes is 3.4 units, and no counter is under 5.** That is the
 * rule that decides how much detail a glyph is allowed, and it is derived rather than chosen: at
 * the 15px and 16px this app draws most icons at, a unit is 0.63px, so two strokes 3.4 units apart
 * leave about 0.6px of ground between them and anything tighter is a grey smudge. Every glyph that
 * used to mush at 16px — the printer, the value table, the number line, the book's spine, the
 * sun's rays — broke it.
 *
 * ── Weight ───────────────────────────────────────────────────────────────────────────────────
 *
 * DESIGN.md §8 asks for "1.5px stroke, 20px box". A fixed `stroke-width="1.5"` on a 24 viewBox does
 * not deliver that: at 20px it draws 1.25px, at 16px 1.00px, at 15px 0.94px — a hairline, which is
 * why the old set read as washed-out grey rather than as drawing. `strokeFor()` solves for the
 * spec instead of restating it, so 1.5px is what lands on the screen at every size from 14 to 26,
 * which is where all but four of the app's icons live.
 *
 * ── The family rule ──────────────────────────────────────────────────────────────────────────
 *
 * An instrument is an outline; a mark is solid. A pencil, an eraser, a set square, a compass, a
 * bin, a sheet — things made of wood and brass that you hold — are drawn in stroke. Graphite on
 * paper, a construction's argument points, an arrowhead, the pointer, a note head, a binary state
 * (sun/moon, playing/paused) — things the app puts down — are drawn solid. Every glyph therefore
 * carries at least one solid mass, which is what stops the set greying out at small sizes, and the
 * two treatments are a rule rather than an accident.
 *
 * Helper geometry is dashed, everywhere, because construction ink is always dashed on the sheet
 * (DESIGN.md §2). The icons say what the canvas says.
 *
 * Nothing here names a colour: strokes and fills are `currentColor`, so a glyph tints with the
 * armed accent, dims with `--ink-faint` when disabled, and inverts nothing between themes — there
 * is no `fill-rule` anywhere in the set, so there is no rule that can flip.
 */

export interface Glyph {
  /** Stroked outline — the instrument. */
  d?: string[];
  /** Stroked and dashed — helper geometry, the lines you delete afterwards. */
  dash?: string[];
  /** Solid path — the mark. */
  fill?: string[];
  /** Solid disc — a point, a joint, a note head. Radius follows the stroke. */
  dots?: [number, number][];
  /** Multiplies the dot radius, for the two glyphs whose dots are not points. */
  dotScale?: number;
  /** Solid rounded bar `[x, y, w, h]` — the one way to draw a weight that is not the stroke. */
  bars?: [number, number, number, number][];
}

/**
 * Stroke width in user units for a glyph rendered at `size` px.
 *
 * Constant 1.5px on screen from 14px to 26px; 1.3px at 12px, where 1.5 would close every counter;
 * and above 26px the units hold and the stroke grows with the drawing, the way a larger cut of a
 * typeface does.
 */
export const strokeFor = (size: number): number =>
  Math.min(2.6, Math.max(1.35, (24 * 1.5) / Math.max(1, size)));

/**
 * A point is 3.9px across at every size.
 *
 * Tying it to the stroke rather than fixing it in units is what keeps a construction's arguments
 * reading as points instead of as lumps on the line: at 12px a fixed 2-unit radius is barely wider
 * than the stroke it sits on.
 */
export const dotRadius = (stroke: number, scale = 1): number => Math.max(1.95, stroke * 1.3) * scale;

/** One pencil, drawn once. `pen`, `pen-pencil` and the radio's rename affordance are all it. */
const PENCIL: Glyph = {
  d: ['M16.36 2.76L20.04 6.44L11.55 14.93L6.6 16.2L7.87 11.25Z', 'M10.13 8.99L13.81 12.67'],
  fill: ['M6.6 16.2L8.85 15.63L7.17 13.95Z'],
};

/** The angle marks: an arc at the vertex plus the two rays it is measured between. */
const ANGLE = ['M4.8 19.2H19.8', 'M4.8 19.2L17 6.8', 'M12.2 19.2A7.4 7.4 0 0010 14'];

/** A full circle as two half-arcs — the one idiom in the set, so every ring is the same ring. */
const ring = (cx: number, cy: number, r: number): string =>
  `M${cx} ${cy - r}a${r} ${r} 0 100 ${2 * r}a${r} ${r} 0 100 ${-2 * r}Z`;

export const ICONS: Record<string, Glyph> = {
  // ── Pointer ────────────────────────────────────────────────────────────────────────────────
  //
  // Solid. An outlined arrow cursor was the weakest mark in the old set, and it is the one glyph
  // in the app that has to read at a glance at 12px.
  select: { fill: ['M6.4 3.6L6.4 19.9L10.6 15.9L13.2 21.2L16.1 19.8L13.5 14.6H19.1Z'] },

  // ── The pens ───────────────────────────────────────────────────────────────────────────────
  //
  // Five pens that differ in use are five drawings, not one pencil five times. They share a frame
  // — an instrument raked across the box on the same 45° axis — and differ where it matters: the
  // marker is a chisel and a slab of felt, the fineliner is a needle, the construction pen's mark
  // is dashed because that is the only kind of line it makes, and the correction pen carries the
  // cross you reach for it to draw.
  pen: PENCIL,
  'pen-pencil': PENCIL,
  'pen-marker': {
    d: ['M14.49 4.11L19.29 8.91L11.51 16.69L6.71 11.89Z'],
    fill: ['M6.71 11.89L11.51 16.69L8.6 19.2L4.2 14.4Z'],
  },
  'pen-fineliner': {
    d: ['M17.26 4.06L19.94 6.74L10.98 15.7L5.4 18.6L8.3 13.02Z', 'M10 11.32L12.68 14'],
    fill: ['M5.4 18.6L6.89 17.83L6.17 17.11Z'],
  },
  'pen-construction': {
    d: ['M16.23 2.83L19.77 6.37L11.94 14.2L7.2 15.4L8.4 10.66Z'],
    fill: ['M7.2 15.4L9.34 14.86L7.74 13.26Z'],
    dash: ['M3.2 19.6H20.8'],
  },
  'pen-correction': {
    d: [
      'M16.23 2.83L19.77 6.37L11.94 14.2L7.2 15.4L8.4 10.66Z',
      'M10.8 17L15.6 21.8', 'M15.6 17L10.8 21.8',
    ],
    fill: ['M7.2 15.4L9.34 14.86L7.74 13.26Z'],
  },
  // The compartment's own mark: a nib, with the slit and the breather hole a nib has.
  nib: {
    d: ['M12 20.8L7.4 12V5.8A1.6 1.6 0 019 4.2H15A1.6 1.6 0 0116.6 5.8V12Z', 'M12 20.8V13.6'],
    dots: [[12, 10]],
    dotScale: 0.72,
  },

  // ── The eraser ─────────────────────────────────────────────────────────────────────────────
  //
  // The block, and the stroke it is taking out, which stops at the block's leading edge. An
  // eraser that is only a rhombus is a rhombus; an eraser interrupting a line is an eraser.
  eraser: {
    d: ['M17.8 5.5L20.8 12.9L9 17.7L6 10.3Z', 'M10 8.7L13 16.1'],
    bars: [[2.8, 19.2, 4.4, 2.1]],
  },

  // ── Primitives — the tool is the shape ─────────────────────────────────────────────────────
  //
  // A circle drawn centre-out shows its centre; a rectangle dragged corner to corner shows the two
  // corners you drag. The dots are not decoration, they are the gesture.
  line: { d: ['M5.4 18.6L18.6 5.4'], dots: [[5.4, 18.6], [18.6, 5.4]] },
  circle: { d: [ring(12, 12, 7.6)] },
  ellipse: { d: ['M12 6.2a8.4 5.8 0 100 11.6a8.4 5.8 0 100-11.6Z'] },
  rect: { d: ['M4.6 6.4H19.4V17.6H4.6Z'], dots: [[4.6, 6.4], [19.4, 17.6]], dotScale: 0.85 },
  polygon: { d: ['M12 4L20 9.8L16.9 19.2H7.1L4 9.8Z'], dots: [[12, 4]] },
  // Centre, then the sweep — a sector, which is what the tool actually places.
  arc: {
    d: ['M4.4 17.6A7.9 7.9 0 0119.6 17.6'],
    dash: ['M4.4 17.6H19.6'],
    dots: [[4.4, 17.6], [19.6, 17.6]],
  },

  // ── Marks ──────────────────────────────────────────────────────────────────────────────────
  arrow: {
    d: ['M4.6 19.4L15.8 8.2'],
    fill: ['M20.2 3.8L17.6 10.5L13.5 6.4Z'],
    dots: [[4.6, 19.4]],
  },
  angle: { d: ANGLE, dots: [[4.8, 19.2]] },
  rightangle: {
    d: ['M4.8 19.2H19.8', 'M4.8 19.2V4.8', 'M9.8 19.2V14.2H4.8'],
    dots: [[4.8, 19.2]],
  },
  brace: {
    d: [
      'M3.4 7.8A3 3 0 016.4 10.8V13.4A3 3 0 009.4 16.4A2.6 2.6 0 0112 19'
      + 'A2.6 2.6 0 0114.6 16.4A3 3 0 0017.6 13.4V10.8A3 3 0 0120.6 7.8',
    ],
    dots: [[3.4, 7.8], [20.6, 7.8]],
  },

  // ── Diagrams ───────────────────────────────────────────────────────────────────────────────
  axes: {
    d: ['M5.4 6.6V18.6H17.2', 'M9.4 15.2C13.8 15.2 14.6 6.8 19.8 6.8'],
    fill: ['M5.4 2.9L3.5 6.9H7.3Z', 'M21.1 18.6L17.2 20.5V16.7Z'],
  },
  // Intervals, open and closed — which is the whole reason this tool exists.
  numberline: {
    d: ['M3.2 12H13.6', 'M19.2 12H20.8', ring(16.4, 12, 2.8)],
    bars: [[8, 10.9, 5.6, 2.2]],
    dots: [[8, 12]],
  },
  table: {
    d: ['M3.8 6.4A1.8 1.8 0 015.6 4.6H18.4A1.8 1.8 0 0120.2 6.4V17.6A1.8 1.8 0 0118.4 19.4H5.6A1.8 1.8 0 013.8 17.6Z',
      'M3.8 9.8H20.2', 'M12 9.8V19.4'],
  },

  // ── Notation ───────────────────────────────────────────────────────────────────────────────
  //
  // A radical, not a letter T. This tool types mathematics, and the radical is the one mark that
  // says mathematics at 16px without spelling a word in a language.
  text: { d: ['M3.6 12.9H6.6L9.7 19.4L13.5 4.8H20.6'] },
  math: { d: ['M3.6 12.9H6.6L9.7 19.4L13.5 4.8H20.6'] },
  // Steps aligned on a column, and the margin that alignment leaves.
  derivation: {
    d: ['M3.6 6.6H9.8', 'M15 6.6H20.4', 'M3.6 12H9.8', 'M15 12H18.6', 'M3.6 17.4H9.8', 'M15 17.4H19.6'],
    dash: ['M12.4 4.4V19.6'],
  },

  // ── The instruments the app is named after ─────────────────────────────────────────────────
  //
  // The set square rests on a straightedge, because that is how it is used and because a bare
  // triangle is already the polygon tool. The compass has a hinge, which is the detail that stops
  // two legs and an arc from reading as a capital A.
  setsquare: { d: ['M5 16.2V3.4L17.8 16.2Z', 'M2.8 20.6H21.2'] },
  compass: {
    d: ['M12 4.8L6.4 16.4', 'M12 4.8L17.6 16.4', 'M5.2 19.4A11 11 0 0018.8 19.4'],
    dots: [[12, 4.2]],
  },
  ruler: {
    d: ['M3.4 8.2H20.6V15.8H3.4Z', 'M7.7 8.2V11.6', 'M12 8.2V13.4', 'M16.3 8.2V11.6'],
  },
  protractor: {
    d: ['M3.6 18.4A8.4 8.4 0 0120.4 18.4', 'M2.8 18.4H21.2', 'M12 18.4L16.2 11.1'],
    dots: [[12, 18.4]],
  },

  // ── App chrome ─────────────────────────────────────────────────────────────────────────────
  grid: {
    dots: [[6, 6], [12, 6], [18, 6], [6, 12], [12, 12], [18, 12], [6, 18], [12, 18], [18, 18]],
    dotScale: 0.72,
  },
  paper: {
    d: [
      'M14.6 3.2H6.8A1.8 1.8 0 005 5V19A1.8 1.8 0 006.8 20.8H17.2A1.8 1.8 0 0019 19V7.6Z',
      'M14.6 3.2V6.2A1.4 1.4 0 0016 7.6H19',
      'M8.4 12.4H15.6', 'M8.4 16.4H13',
    ],
  },
  sheets: {
    d: [
      'M10 8.4H19A1.8 1.8 0 0120.8 10.2V19.6A1.8 1.8 0 0119 21.4H10A1.8 1.8 0 018.2 19.6V10.2A1.8 1.8 0 0110 8.4Z',
      'M15.6 8.4V4.6A1.8 1.8 0 0013.8 2.8H5A1.8 1.8 0 003.2 4.6V13.8A1.8 1.8 0 005 15.6H8.2',
    ],
  },
  /* Three rules at 5 units apart — comfortably over the 3.4 minimum, so it stays three lines
     rather than a smudge at 16px. Full width, because a short hamburger reads as a list. */
  menu: {
    d: ['M4.2 7.4H19.8', 'M4.2 12H19.8', 'M4.2 16.6H19.8'],
  },
  /* Two figures, the second set back and clipped by the first — a group, not a crowd. The heads
     clear each other by 3.6 units; a third figure does not fit the grid at this weight. */
  people: {
    d: [
      'M3.3 19.4C3.3 16.2 5.9 14.2 9.1 14.2C12.3 14.2 14.9 16.2 14.9 19.4',
      'M16.1 6.0C17.8 6.4 18.9 7.8 18.9 9.4C18.9 11.0 17.8 12.4 16.1 12.8',
      'M17.4 14.6C19.5 15.3 20.7 17.0 20.7 19.4',
    ],
    // The near head is FILLED, not outlined. The family rule is that every glyph carries a solid
    // mass — it is what stops the set greying out at 16px — and a person is the one part of this
    // drawing that can hold one without becoming a blob.
    fill: [ring(9.1, 8.4, 3.1)],
  },
  home: {
    d: [
      'M3.4 10.6L12 3.6L20.6 10.6V19A1.8 1.8 0 0118.8 20.8H5.2A1.8 1.8 0 013.4 19Z',
      'M9.2 20.8V15.2H14.8V20.8',
    ],
  },
  book: {
    d: [
      'M12 7.2C10.2 5.6 7.8 4.8 4.4 4.8V17.2C7.8 17.2 10.2 18 12 19.6C13.8 18 16.2 17.2 19.6 17.2V4.8C16.2 4.8 13.8 5.6 12 7.2Z',
      'M12 7.2V19.6',
    ],
  },
  clock: { d: [ring(12, 12, 8), 'M12 7.2V12L15.6 14.4'] },
  printer: {
    d: [
      'M7.4 9V4.6A1 1 0 018.4 3.6H15.6A1 1 0 0116.6 4.6V9',
      'M7.4 17.4H5.2A1.6 1.6 0 013.6 15.8V10.6A1.6 1.6 0 015.2 9H18.8A1.6 1.6 0 0120.4 10.6V15.8A1.6 1.6 0 0118.8 17.4H16.6',
      'M7.4 14.4H16.6V20.4H7.4Z',
    ],
  },
  keep: { d: ['M7 3.6H17A1.6 1.6 0 0118.6 5.2V20.6L12 16.4L5.4 20.6V5.2A1.6 1.6 0 017 3.6Z'] },
  'keep-on': { fill: ['M7 3.6H17A1.6 1.6 0 0118.6 5.2V20.6L12 16.4L5.4 20.6V5.2A1.6 1.6 0 017 3.6Z'] },
  // Light and dark are states of the sheet, not instruments, so they are solid.
  sun: {
    fill: [ring(12, 12, 3.9)],
    d: [
      'M12 5.9V2.7', 'M12 18.1V21.3', 'M5.9 12H2.7', 'M18.1 12H21.3',
      'M7.7 7.7L5.4 5.4', 'M16.3 16.3L18.6 18.6', 'M7.7 16.3L5.4 18.6', 'M16.3 7.7L18.6 5.4',
    ],
  },
  moon: { fill: ['M20.2 14.6A8.4 8.4 0 119.4 3.8A9.6 9.6 0 0020.2 14.6Z'] },
  download: {
    d: ['M12 3.4V10', 'M4.6 17.4V19A1.8 1.8 0 006.4 20.8H17.6A1.8 1.8 0 0019.4 19V17.4'],
    fill: ['M12 14.9L7.9 9.6H16.1Z'],
  },
  chevron: { d: ['M6 9.8L12 15.8L18 9.8'] },
  caret: { d: ['M6 9.8L12 15.8L18 9.8'] },
  plus: { d: ['M12 5.2V18.8', 'M5.2 12H18.8'] },
  add: { d: ['M12 5.2V18.8', 'M5.2 12H18.8'] },
  close: { d: ['M6 6L18 18', 'M18 6L6 18'] },
  check: { d: ['M5 12.6L9.7 17.4L19 7'] },
  trash: {
    d: [
      'M4.4 6.8H19.6',
      'M9.4 6.8V5A1.4 1.4 0 0110.8 3.6H13.2A1.4 1.4 0 0114.6 5V6.8',
      'M6.6 6.8L7.5 19.2A1.8 1.8 0 009.3 20.9H14.7A1.8 1.8 0 0016.5 19.2L17.4 6.8',
      'M9.7 10.8V16.4', 'M14.3 10.8V16.4',
    ],
  },
  remove: {
    d: [
      'M4.4 6.8H19.6',
      'M9.4 6.8V5A1.4 1.4 0 0110.8 3.6H13.2A1.4 1.4 0 0114.6 5V6.8',
      'M6.6 6.8L7.5 19.2A1.8 1.8 0 009.3 20.9H14.7A1.8 1.8 0 0016.5 19.2L17.4 6.8',
    ],
  },
  help: {
    d: ['M8.6 9.9A3.6 3.6 0 1112.4 13.6V15.6'],
    dots: [[12.4, 19.2]],
  },
  search: { d: [ring(10.6, 10.6, 6.2), 'M15.2 15.2L20.4 20.4'] },
  undo: {
    d: ['M20.4 19.2A7.6 7.6 0 0012.8 11.6H9.8'],
    fill: ['M4.4 11.6L10.2 8.3V14.9Z'],
  },
  // Two crop marks: the L of a corner and its opposite, which is the mark itself rather than a
  // picture of a picture.
  crop: {
    d: ['M7.4 3.2V16.6H20.8', 'M3.2 7.4H16.6V20.8'],
    // The kept window, solid: the family rule is that every glyph carries a mass, and on a crop
    // mark the mass is the part of the picture that survives.
    fill: ['M9.6 9.6H16.6V16.6H9.6Z'],
  },
  // The same arc mirrored about x=12, so the pair reads as one control with two directions.
  redo: {
    d: ['M3.6 19.2A7.6 7.6 0 0111.2 11.6H14.2'],
    fill: ['M19.6 11.6L13.8 8.3V14.9Z'],
  },
  // Three weights, and they are three weights. Rounded bars, not the stroke, because a set with
  // one stroke width cannot draw a heavier line by drawing another line.
  weight: { bars: [[3.6, 4.8, 16.8, 1.3], [3.6, 9.6, 16.8, 3], [3.6, 15.8, 16.8, 5.4]] },
  // Ink in the ring the rail already shows it in.
  ink: { d: [ring(12, 12, 7.4)], fill: [ring(12, 12, 3.8)] },

  // ── The instruments on the desk ────────────────────────────────────────────────────────────
  //
  // The rack in `src/lib/instruments.ts` summons these; they are objects on a desk rather than
  // tools in a hand, so each is the thing itself: a keyed box, a curve, a fraction.
  calculator: {
    d: [
      'M5 5A2 2 0 017 3H17A2 2 0 0119 5V19A2 2 0 0117 21H7A2 2 0 015 19Z',
      'M12 13.4V18.6', 'M9.4 16H14.6',
    ],
    bars: [[7.6, 6, 8.8, 3.4]],
  },
  // The plotter: a curve, and one point read off it.
  wave: {
    d: ['M3.4 12C5.4 5.2 9.4 5.2 11.4 12C13.4 18.8 17.4 18.8 19.4 12'],
    dots: [[7.4, 6.9]],
  },
  // The formula sheet. A fraction is set with its bar heavier than its terms, so it is here too.
  formula: {
    d: [
      'M3.6 5.4A1.8 1.8 0 015.4 3.6H18.6A1.8 1.8 0 0120.4 5.4V18.6A1.8 1.8 0 0118.6 20.4H5.4A1.8 1.8 0 013.6 18.6Z',
      'M9 7.8H15', 'M9 16.2H15',
    ],
    bars: [[7.2, 11.4, 9.6, 1.6]],
  },

  // ── Construction ───────────────────────────────────────────────────────────────────────────
  //
  // One idea, applied without exception: the ARGUMENTS are solid dots, the RESULT is a stroke, and
  // a helper the construction only needs in order to exist is dashed. The picture says what the
  // tool will ask you for before you have armed it.
  //
  // The point was here, and now it is there.
  hand: {
    d: [ring(7.4, 14.8, 2.4)],
    dash: ['M9.8 13.6C11.8 11 13.6 10 15.2 9.6'],
    dots: [[16.8, 9.2]],
  },
  point: {
    d: ['M12 3.4V6.2', 'M12 17.8V20.6', 'M3.4 12H6.2', 'M17.8 12H20.6'],
    dots: [[12, 12]],
  },
  pointon: { d: ['M3.4 15.8C7 9.4 17 9.4 20.6 15.8'], dots: [[12, 11]] },
  // Two points and the one between them. The constructed point is the ringed one.
  midpoint: {
    d: ['M4.6 12H19.4', ring(12, 12, 3.6)],
    dots: [[4.6, 12], [19.4, 12], [12, 12]],
  },
  segment: {
    d: ['M5.2 18.4L18.8 5.6', 'M10.6 10.6L13.5 13.3'],
    dots: [[5.2, 18.4], [18.8, 5.6]],
  },
  cline: { d: ['M3 21L21 3'], dots: [[8.4, 15.6], [15.6, 8.4]] },
  ray: {
    d: ['M5.6 18.4L17.2 6.8'],
    fill: ['M20.6 3.4L18.6 9.2L14.8 5.4Z'],
    dots: [[5.6, 18.4], [12.4, 11.6]],
  },
  perp: {
    d: ['M3.4 18H20.6', 'M12 5.2V18', 'M15.6 18V14.4H12'],
    dots: [[12, 5.2]],
  },
  para: {
    d: ['M3.4 8.2H20.6', 'M3.4 16.6H20.6', 'M14.4 6.6L16.2 8.2L14.4 9.8', 'M14.4 15L16.2 16.6L14.4 18.2'],
    dots: [[7.4, 8.2]],
  },
  ccircle: { d: [ring(12, 12, 7.6), 'M12 12H19.6'], dots: [[12, 12], [19.6, 12]] },
  ccircle3: { d: [ring(12, 12, 7.6)], dots: [[12, 4.4], [18.6, 15.8], [5.4, 15.8]] },
  intersect: {
    d: ['M4.2 6.2L19.8 17.8', 'M4.2 17.8L19.8 6.2'],
    dots: [[12, 12]],
  },
  cangle: { d: ANGLE, dots: [[4.8, 19.2], [19.8, 19.2], [17, 6.8]] },
  cpolygon: {
    d: ['M12 4.6L19.8 18.6H4.2Z'],
    dots: [[12, 4.6], [19.8, 18.6], [4.2, 18.6]],
  },

  // ── Transport ──────────────────────────────────────────────────────────────────────────────
  //
  // Playing and paused are states, so they are solid; the things you operate on a stream — the
  // loop, the shuffle, the volume — are outlines. Same rule as the rest of the set, not a second.
  play: { fill: ['M8.2 4.8L19.4 12L8.2 19.2Z'] },
  pause: { bars: [[7.4, 5, 3.2, 14], [13.4, 5, 3.2, 14]] },
  previous: { fill: ['M17.6 5L8 12L17.6 19Z'], bars: [[3, 5, 2.6, 14]] },
  next: { fill: ['M6.4 5L16 12L6.4 19Z'], bars: [[18.4, 5, 2.6, 14]] },
  loop: {
    d: ['M8.6 6.8H15.2A3.6 3.6 0 0118.8 10.4V12.2', 'M15.4 17.2H8.8A3.6 3.6 0 015.2 13.6V11.8'],
    fill: ['M18.8 15.8L16.4 11.6H21.2Z', 'M5.2 8L7.6 12.2H2.8Z'],
  },
  loopOne: {
    d: [
      'M8.6 6.8H15.2A3.6 3.6 0 0118.8 10.4V12.2', 'M15.4 17.2H8.8A3.6 3.6 0 015.2 13.6V11.8',
      'M10.6 10.6L12.4 9.4V14.6',
    ],
    fill: ['M18.8 15.8L16.4 11.6H21.2Z', 'M5.2 8L7.6 12.2H2.8Z'],
  },
  shuffle: {
    d: ['M3.4 7.4H6.6L16 16.6H18.4', 'M3.4 16.6H6.6L9.6 13.6', 'M13.4 10.4L16 7.4H18.4'],
    fill: ['M21.6 7.4L17.2 9.6V5.2Z', 'M21.6 16.6L17.2 18.8V14.4Z'],
  },
  volume: {
    d: ['M14.8 9.4A4 4 0 0114.8 14.6', 'M17.8 6.6A8 8 0 0117.8 17.4'],
    fill: ['M3.6 9.2H7.2L11.6 5.2V18.8L7.2 14.8H3.6Z'],
  },
  muted: {
    d: ['M15.2 9.6L20.4 14.8', 'M20.4 9.6L15.2 14.8'],
    fill: ['M3.6 9.2H7.2L11.6 5.2V18.8L7.2 14.8H3.6Z'],
  },
  // A note head is solid in the notation this app renders, so it is solid here.
  note: {
    d: ['M9.4 17.2V5.4L19.4 3.5V15.3'],
    dots: [[6.8, 17.2], [16.8, 15.3]],
    dotScale: 1.35,
  },
  pencil: PENCIL,

  // ── The plane's own instruments ─────────────────────────────────────────────────────────────
  //
  // Three glyphs the graphing plane needs and the sheet did not: what is shown, what is mirrored,
  // what is turned. Helper geometry stays dashed, the mass stays solid, and the mirror's two
  // triangles sit 3.6 units off the axis so the ground between them survives 16px.
  eye: {
    d: ['M3.4 12C7 6.6 17 6.6 20.6 12'],
    dash: ['M3.4 12C7 17.4 17 17.4 20.6 12'],
    dots: [[12, 15]],
  },
  mirror: {
    d: ['M8.4 7.4L3.6 12L8.4 16.6Z'],
    dash: ['M12 3.4V20.6'],
    fill: ['M15.6 7.4L20.4 12L15.6 16.6Z'],
  },
  // ── The workshop, added with the rebuilt rail ───────────────────────────────────────────────
  //
  // Two families. The SHAPES are the figure they place — a hexagon is a hexagon, a wedge is a
  // wedge — because a shape tool whose icon is a metaphor is a shape tool nobody finds twice. The
  // TRANSFORMS are the operation acting on a mark: the thing being moved is drawn solid and what
  // it is being moved against — an axis, an edge, a stack — is drawn in stroke or dashed.
  polyline: { d: ['M4 18L9 8L15 15L20 5'], dots: [[4, 18], [20, 5]] },
  ngon: { d: ['M12 3.6L18.9 7.8V16.2L12 20.4L5.1 16.2V7.8Z'], dots: [[12, 3.6]] },
  sector: { d: ['M4.4 19.6V5.6A14 14 0 0118.4 19.6Z'], dots: [[4.4, 19.6]] },
  ticks: {
    d: ['M3.2 12H20.8', 'M10 8V16', 'M14 8V16'],
    dots: [[3.2, 12], [20.8, 12]],
  },

  // A turn is three quarters of a ring and the head that says which way round.
  rotate: {
    d: ['M12 4.2A7.8 7.8 0 1 0 19.8 12'],
    fill: ['M16.8 10.4H22.8L19.8 16Z'],
  },
  'rotate-left': {
    d: ['M12 4.2A7.8 7.8 0 1 1 4.2 12'],
    fill: ['M7.2 10.4H1.2L4.2 16Z'],
  },
  // The mirror line is helper geometry, so it is dashed — the same rule the canvas obeys.
  'flip-x': {
    d: ['M8.6 6.4V17.6L3.2 12Z'],
    dash: ['M12 3.2V20.8'],
    fill: ['M15.4 6.4V17.6L20.8 12Z'],
  },
  'flip-y': {
    d: ['M6.4 8.6H17.6L12 3.2Z'],
    dash: ['M3.2 12H20.8'],
    fill: ['M6.4 15.4H17.6L12 20.8Z'],
  },
  align: {
    d: ['M3.6 3.6V20.4'],
    bars: [[7, 6.4, 13.4, 3.6], [7, 14, 8.8, 3.6]],
  },
  distribute: { bars: [[3.4, 4.4, 3.4, 15.2], [10.3, 4.4, 3.4, 15.2], [17.2, 4.4, 3.4, 15.2]] },
  duplicate: { d: ['M3.6 3.6H14.4V14.4H3.6Z'], fill: ['M9.6 9.6H20.4V20.4H9.6Z'] },
  front: { d: ['M6.4 14.6H17.6V19.4H6.4Z'], fill: ['M4.6 4.6H19.4V11.2H4.6Z'] },
  back: { d: ['M6.4 4.6H17.6V9.4H6.4Z'], fill: ['M4.6 12.8H19.4V19.4H4.6Z'] },

  // ── The figures the Diagrams compartment places whole ───────────────────────────────────────
  signtable: {
    d: ['M3.4 6.8H20.6'],
    bars: [[4.6, 13.2, 5.4, 1.8], [14, 13.2, 5.4, 1.8], [15.85, 11.1, 1.7, 6]],
  },
  tree: {
    d: ['M4.4 12H10', 'M10 12L19.6 4.6', 'M10 12H19.6', 'M10 12L19.6 19.4'],
    dots: [[4.4, 12], [19.6, 4.6], [19.6, 12], [19.6, 19.4]],
  },
  venn: {
    d: [ring(9.2, 12, 6.4), ring(14.8, 12, 6.4)],
    fill: ['M12 7.4a3.6 4.6 0 100 9.2a3.6 4.6 0 100-9.2Z'],
  },
  matrix: {
    d: ['M7.4 4.2H4.6V19.8H7.4', 'M16.6 4.2H19.4V19.8H16.6'],
    bars: [[10.6, 7.8, 2.8, 2.8], [10.6, 14, 2.8, 2.8]],
  },
  boxplot: {
    d: ['M7.8 7.4H16.6V16.6H7.8Z', 'M3.4 12H7.8', 'M16.6 12H21', 'M3.4 8.6V15.4', 'M21 8.6V15.4'],
    bars: [[11.5, 7.4, 1.4, 9.2]],
  },
  histogram: {
    d: ['M3.2 20.4H21'],
    bars: [[4.7, 13.8, 3.1, 6.6], [11.2, 8, 3.1, 12.4], [17.7, 11.2, 3.1, 9.2]],
  },

  // The corrector: a wavering line and the clean one it becomes. Stroke above, solid mass below,
  // which is the family rule read literally — the hand's mark and the app's.
  tidy: {
    // The wave's own extrema sit at 8.4 ± 2.25, so with a 1.5 stroke its underside reaches 11.4
    // and the bar's 15.6 leaves 4.2 — clear of the 3.4 minimum. Drawn tighter first, at 2.55, and
    // it smudged into one grey mass at the 16px the rail draws it at.
    d: ['M3.6 8.4C5.3 5.4 7.3 11.4 9.3 8.4C11.3 5.4 13.3 11.4 15.3 8.4C16.6 6.4 18.9 7.4 20.4 8.9'],
    bars: [[3.6, 15.6, 16.8, 2.6]],
  },

  // A note in words: the bubble drawn as one path with its pointer, and the writing inside it as
  // the solid mass the family rule asks for. One bar, not three — at 16px three lines of "text"
  // 1.8 units apart is a grey smudge, which is the clearance rule deciding how much detail a
  // glyph is allowed. `note` is already the radio's note head, so this one is `bubble`.
  bubble: {
    d: ['M3.6 4.6H20.4V15.4H12.4L7.8 19.8V15.4H3.6Z'],
    bars: [[7.8, 8.8, 8.2, 2.4]],
  },
};

export const ICON_NAMES: readonly string[] = Object.keys(ICONS);
